import { useEffect, useMemo, useState } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { StepContent } from '../StepContent';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { useUserLoggedIn } from '@modules/user';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
    getHdeStepSectionClassName,
    getHdeStepTitleClassName,
} from '../hde-option-styles';

/** Lightweight professional profile from Me.Professionals */
interface ProfessionalProfile {
    ID: string;
    Title?: string | null;
    URLSegment?: string | null;
    Website?: string | null;
    PrimaryDomain?: string | null;
    CompanyLogo?: unknown;
}

interface BusinessProfilePickerStepData {
    professionals: ProfessionalProfile[];
    selectedProfessionalId: string | null;
}

/**
 * Renders the logged-in user's business profiles for selection.
 * Shown when user has one or more profiles; user must explicitly select before continuing.
 */
export function BusinessProfilePickerStep({
    setIsValid,
    onDataChange,
    initialData,
}: StepContentProps) {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const { user } = useUserLoggedIn();
    const data = initialData as BusinessProfilePickerStepData | undefined;
    const professionals = data?.professionals ?? [];
    const [selectedId, setSelectedId] = useState<string | null>(
        data?.selectedProfessionalId ?? null,
    );
    const firstName = useMemo(() => {
        const currentUser = user as { FirstName?: string } | null;

        return currentUser?.FirstName?.trim() || 'there';
    }, [user]);

    const isValid = selectedId !== null && professionals.length > 0;

    useEffect(() => {
        setIsValid(isValid);
        onDataChange?.({ selectedProfessionalId: selectedId });
    }, [selectedId, isValid, setIsValid, onDataChange]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <StepContent>
                <div className={getHdeStepSectionClassName()}>
                    <h2 className={getHdeStepTitleClassName()}>
                        Looking forward to see you, {firstName}!
                    </h2>
                    <p className="max-w-2xs mb-8 text-sm leading-tight text-[#161616]">
                        Which business profile are you representing?
                    </p>
                    <div className="w-full space-y-3">
                        <label
                            className={getHdeFieldLabelClassName()}
                            htmlFor="business-profile-select"
                        >
                            Select company
                        </label>
                        <Select
                            value={selectedId ?? ''}
                            onValueChange={(value) => setSelectedId(value)}
                        >
                            <SelectTrigger
                                id="business-profile-select"
                                className={getHdeInputClassName()}
                            >
                                <SelectValue placeholder="Select company" />
                            </SelectTrigger>
                            <SelectContent
                                className="z-10000"
                                side={isMobile ? 'top' : 'bottom'}
                            >
                                {professionals.map((prof) => (
                                    <SelectItem key={prof.ID} value={prof.ID}>
                                        <span className="text-base">
                                            {prof.Title ?? 'Untitled'}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </StepContent>
        </div>
    );
}
